import { Image } from "@cryptors/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import ArrowBack from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Divider, IconButton, TextField } from "@mui/material";
import { Edit } from "@refinedev/mui";
import { useEffect, useMemo } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import {
  useDeleteHint,
  useGetHints,
  usePatchHint,
  usePostHint,
} from "@/api/hints.api";
import UploadField from "@/components/forms/UploadField";
import { getTranslation } from "@/utils/translation";

export const HintsSchema = z.object({
  hints: z.array(
    z.object({
      id: z.string().nullable(),
      index: z.number().min(1),
      rewardIfNotUsed: z.number().min(0),
      descriptionTranslation: z.object({ value: z.string().min(1).optional() }),
      cryptorId: z.number().min(1),
      image: z.object({ url: z.string().min(1).nullish() }).nullish(),
    }),
  ),
});

export type HintsFormData = z.infer<typeof HintsSchema>;

const HintsEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const cryptorId = searchParams.get("cryptorId");

  const { data, isLoading } = useGetHints(Number(cryptorId));
  const { mutateAsync: postHint } = usePostHint();
  const { mutateAsync: patchHint } = usePatchHint();
  const { mutateAsync: deleteHint } = useDeleteHint();

  const formatedData = useMemo(
    () =>
      data.map((hint) => ({
        id: hint.id as string | null,
        index: Number(hint.index),
        rewardIfNotUsed: Number(hint.rewardIfNotUsed),
        descriptionTranslation: {
          value: getTranslation(hint.descriptionTranslation)?.value || "",
        },
        cryptorId: Number(cryptorId),
        image: hint.image as Image | null,
      })),
    [data, cryptorId],
  );

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
    setError,
  } = useForm<HintsFormData>({
    resolver: zodResolver(HintsSchema),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  useEffect(() => {
    reset({ hints: formatedData });
  }, [reset, formatedData]);

  const {
    fields: hints,
    append: appendHint,
    remove: removeHint,
  } = useFieldArray({
    control,
    name: "hints",
    keyName: "key",
  });

  return (
    <Edit
      goBack={
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
      }
      headerButtons={() => null}
      isLoading={isLoading}
      saveButtonProps={{
        onClick: handleSubmit((data) => {
          for (const hint of data.hints) {
            const { id, image, ...hintWithoutId } = hint;
            const imageFormated =
              image && image.url ? { url: image.url } : null;

            if (id) {
              patchHint({
                id,
                data: { ...hintWithoutId, image: imageFormated },
              });
            } else {
              postHint({ ...hintWithoutId, image: imageFormated });
            }
          }
        }),
        disabled: !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {hints.map((hint, index) => (
          <Box key={hint.id || `new-hint-${index}`}>
            <Box display="flex" flexDirection="row" gap={3}>
              <Box marginY={2}>
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    if (hint.id) {
                      deleteHint(hint.id);
                    } else {
                      removeHint(index);
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box display="flex" flexDirection="column" flex={1}>
                <Controller
                  name={`hints.${index}.id`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        margin="normal"
                        label="Id"
                        disabled
                      />
                    );
                  }}
                />
                <Controller
                  name={`hints.${index}.descriptionTranslation.value`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        margin="normal"
                        label="Description"
                        multiline
                        minRows={8}
                      />
                    );
                  }}
                />
              </Box>
              <Box display="flex" flexDirection="column">
                <Controller
                  name={`hints.${index}.index`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(Number(event.target.value))
                        }
                        type="number"
                        margin="normal"
                        label="Index"
                      />
                    );
                  }}
                />
                <Controller
                  name={`hints.${index}.rewardIfNotUsed`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <TextField
                        {...field}
                        onChange={(event) =>
                          field.onChange(Number(event.target.value))
                        }
                        type="number"
                        margin="normal"
                        label="Récompense si pas découvert"
                      />
                    );
                  }}
                />
                <UploadField
                  name={`hints.${index}.image.url` as const}
                  uploadButtonLabel="Upload image"
                  s3FoldersType="hints"
                  control={control}
                  setError={setError}
                />
              </Box>
            </Box>
            <Divider sx={{ marginTop: 3 }} />
          </Box>
        ))}
        <Divider sx={{ marginTop: 3 }}>
          <Button
            onClick={() =>
              appendHint({
                id: null,
                index: hints.length + 1,
                rewardIfNotUsed: 0,
                descriptionTranslation: {
                  value: "",
                },
                cryptorId: Number(cryptorId),
                image: null,
              })
            }
          >
            Ajouter un indice
          </Button>
        </Divider>
      </Box>
    </Edit>
  );
};

export default HintsEdit;
